/**
 * /!\ IMPORTANT NOTE /!\
 *
 * To prevent potential confusion, terms :
 * - '*Points' always refers to max points defined in the subject's structure
 * - '*Score' always refers to the score obtained by the students
 */

import type { GradingMode } from './activity.js';
import type { AppHttpStatus } from './http.js';
import type { LambdaErrorCode, LambdaFunctionName } from './lambda.js';

export type CopiesCorrectionWithChilds = CopiesCorrection & {
  HandwrittenCopy: HandwrittenCopy[];
  Activity?: {
    activityType?: string;
  };
};

export type SendStudentCorrectionResult = {
  emailedCount: number;
  failedEmailCount: number;
};

export type CopiesCorrection = {
  id: string;
  studentClassId: string | null;
  teacherId: string;
  activityId: string | null;
  title: string;

  subjectPath: string | null;
  studentListPath: string | null;
  correctionPath: string | null;
  structurePath: string | null;

  /** Total points of the correction. Should be readonly after structure is validated */
  totalPoints: number;

  /** Status of the structure generation */
  structureStatus: StructureStatus;
  /** Last lambda error for this activity ({ code, from }) */
  lastLambdaError: LastLambdaError | null;
  /** Start date of the QR code validity period for submitting copies */
  qrCodeValidityStart: string | null;
  /** End date of the QR code validity period for submitting copies */
  qrCodeValidityEnd: string | null;

  /** Grading mode of the activity; the AI analysis only works on rubric grading */
  gradingMode?: GradingMode | null;

  /** Path to the class error models (former error analysis, no longer produced) */
  classErrorModelsPath: string | null;
  /** Path to the error taxonomy */
  errorTaxonomyPath: string | null;
  /** Path to the error taxonomy visualization */
  errorTaxonomyVizPath: string | null;

  /** ID of the current analysis execution */
  currentAnalysisExecutionId: string | null;

  /** S3 path to cluster analysis results JSON */
  clusterResultPath: string | null;
  /** ID of the current clustering execution (deduplication) */
  currentClusteringExecutionId: string | null;
  /** S3 path to feature embeddings */
  featuresPath: string | null;

  /** Date of the results publication */
  resultsPublicationDate: string | null;

  createdAt: string;
  updatedAt: string;
};

export type HandwrittenCopy = {
  id: string;
  /** foreign key vers table CopiesCorrection */
  copiesCorrectionId: string; // FIXME: reliquat de jointure pour avoir CopiesCorrection ?
  CopiesCorrection?: CopiesCorrection; // FIXME: Usage à revoir vu qu'on a déjà copiesCorrectionId

  /** Path to the handwritten copy (scanned images in PDF) */
  copyPath: string;
  /** Path to the AI-generated correction JSON file */
  jsonPath?: string;
  name: string; // FIXME: Filename from copyPath (.pdf). extractible depuis copyPath ?
  /** Subject path (PDF file); Used when each student receives a different subject (instead of one global one for everyone) */
  subjectPath: string | null;

  studentId: string | null;
  studentName: string | null;

  /** AI generated mardown content of the correction */
  correction?: string;
  /** Fixed correction after review from teacher */
  correctedCorrection: string | null; // FIXME: rename as 'reviewedCorrection' ?

  /** Original total score obtained by the student from IA-generated correction */
  points?: number; // FIXME: rename as 'totalScore' ?
  /** Total score eventually fixed by the teacher after review */
  correctedPoints: number | null; // FIXME: rename as 'reviewedTotalScore' ?

  correctionStatus: HandwrittenCopyCorrectionStatus;
  /** Last lambda error for this copy ({ code, from }) */
  lastLambdaError: LastLambdaError | null;
  isValidated: boolean; // FIXME: Redondant avec correctionStatus ?

  createdAt: string;
  updatedAt: string;
};

export enum HandwrittenCopyCorrectionStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Finished = 'Finished',
  Validated = 'Validated',
}

export enum StructureStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Generated = 'Generated',
  Validated = 'Validated',
}

export type LastLambdaError = {
  code: LambdaErrorCode;
  /** Pipeline stage — mirrors ``stage`` from lambda_failure_notifier */
  from: LambdaFailureStage;
};

export type ActivityStructureResponse = {
  structure: SubjectStructure | null;
  structureStatus: StructureStatus;
  lastLambdaError: LastLambdaError | null;
} | null;

export enum RubricStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Generated = 'Generated',
  Validated = 'Validated',
}
// See note in ./lambda.ts: the matching runtime constant is defined backend-side
// (in `apps/backend/src/api/copiesCorrection/dto/lambda/LambdaFailed.ts`) to
// avoid forcing Node to load `@repo/api` at runtime.
export type LambdaFailureStage =
  | 'analysis'
  | 'bulk_split'
  | 'clustering'
  | 'correction'
  | 'ocr'
  | 'rubric_generation'
  | 'rubric_refinement'
  | 'similarity_detection'
  | 'structure';

export type LambdaFailurePipelineStep =
  | 'notifier_built_payload'
  | 'api_lambda_failed_received'
  | 'structure_validation_rejected'
  | 'handler_dispatched'
  | 'state_updated'
  | 'lambda_error_persisted'
  | 'websocket_emitted';

export type LambdaFailureTracePaths = {
  copyPath?: string;
  rubricPath?: string;
  solutionPath?: string;
  statementPath?: string;
  structurePath?: string;
  structureJson?: string;
};

export type LambdaFailureTraceLinks = {
  backofficeActivityUrl?: string;
  backofficeCopyUrl?: string;
  teacherActivityUrl?: string;
};

export type LambdaFailureTraceStep = {
  at: string;
  detail?: string;
  step: LambdaFailurePipelineStep;
};

/** Persisted trace for debugging lambda failures without host logs */
export type LambdaFailureTrace = {
  code: LambdaErrorCode;
  copiesCorrectionId: string;
  error?: string;
  condition?: string;
  errorCode?: string;
  functionError?: string;
  functionName?: LambdaFunctionName;
  handwrittenCopyId?: string;
  links?: LambdaFailureTraceLinks;
  paths?: LambdaFailureTracePaths;
  recordedAt: string;
  runId?: string;
  source: 'lambda_failed' | 'structure_validation';
  stage: LambdaFailureStage;
  steps: LambdaFailureTraceStep[];
  traceId: string;
};

export type CopiesCorrectionLambdaErrorPayload = {
  code: LambdaErrorCode;
  copiesCorrectionId: string;
  functionName: LambdaFunctionName;
  handwrittenCopyIds?: string[];
  httpStatus?: AppHttpStatus;
  stage: LambdaFailureStage;
  trace?: Pick<
    LambdaFailureTrace,
    'error' | 'links' | 'paths' | 'recordedAt' | 'steps' | 'traceId'
  >;
};

// =================  SUBJECT STRUCTURE INTERFACES  =================

export type SectionType = 'Exercise' | 'Part' | 'Question' | 'SubQuestion' /* | 'Choice' */;

/** Original interface as returned by the server */
type BaseSection = {
  type: SectionType;
  number: string;
  title: string | null;
  content: string | null;
  points: string | null;
  grade: number | null; // Student score, null during structure analysis phase, but receives number after correction phase
  skill: string | null;
  critical_error: string | null;
  /**
   * Index among its siblings in the source document, stamped on graded
   * structures because their MySQL JSON column does not keep key order.
   */
  document_order?: number;
  /** Graded as usual but left out of the activity's scale (see utils/bonus). */
  bonus?: boolean;
};

export type Section = BaseSection & { subsections?: Record<string, Section> };

export type FlatSection = Omit<BaseSection, 'critical_error' | 'points' | 'document_order'> & {
  id: string;
  key: string;
  criticalError: string | null;
  points: number;
  hasSubsections: boolean;
  fullKeys: string[];
};

export type SubjectStructure = Record<string, Section>;

// =================  COPY CORRECTION INTERFACES  =================

// type CopyCorrectionSection = BaseSection & { type: SectionType | 'Problem' | 'Section' } & {
//   hierarchy_level: number;
//   hierarchy_path: string;
//   full_title?: string;
//   position_in_parent: number;
//   parent_id: string | null;
//   correction_guide: string | null;
// };

// type CCSection = CopyCorrectionSection & { subsections?: Record<string, CCSection> };

// export type CopiesCorrectionCopyStructure = Record<string, CCSection>;
